import { useMapCanvas } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvas";
import { useMapEditorProps } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorProps";
import { applyResourceLabels } from "@gettactic/map-builder/src/hooks/useMapBuilderMappedResourcesData";
import { Pencil } from "lucide-react";
import React from "react";
import { useShallow } from "zustand/react/shallow";
import { toMapValue } from "../document/serialize";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import { MapSvgView } from "../viewer/MapSvgView";

/**
 * Read mode over the canvas: the viewer's rendering of the document as it
 * stands, fed exactly what the map page feeds its viewer (the live
 * resource state, resource labels, the signed-in user), with pan and zoom
 * and the legend. The editor keeps its state underneath; a click brings
 * it back.
 */
export const PreviewOverlay: React.FC = () => {
  const store = useEditorStore();
  const { isPreview, shapes, order, meta } = useEditorSelector(
    useShallow((state) => ({
      isPreview: state.chrome.isPreview,
      shapes: state.shapes,
      order: state.order,
      meta: state.meta
    }))
  );
  const {
    interactiveZones,
    authenticatedUser,
    isPrivacyMode,
    resourceColors,
    zoneColors,
    isDark
  } = useMapCanvas();
  const { resources } = useMapEditorProps();
  // The document as the page would load it, only while the preview is up.
  const value = React.useMemo(() => {
    if (!isPreview) return null;
    const document = toMapValue({ shapes, order, meta });
    return resources ? applyResourceLabels(document, resources) : document;
  }, [isPreview, shapes, order, meta, resources]);
  if (!isPreview || !value) return null;
  return (
    <div
      data-editor-preview
      className="absolute inset-0 z-20 flex flex-col bg-white"
    >
      <div className="pointer-events-none absolute top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1 pr-1 pl-3 text-[11.5px] text-amber-800 shadow-sm">
        <span className="font-medium">Preview</span>
        <span className="text-amber-700/80">what people see on the map</span>
        <button
          type="button"
          className="pointer-events-auto flex items-center gap-1 rounded-full bg-white px-2 py-0.5 font-medium text-amber-900 shadow-sm hover:bg-amber-100"
          onClick={() => store.getState().setChrome({ isPreview: false })}
        >
          <Pencil size={11} />
          Back to editing
        </button>
      </div>
      <MapSvgView
        value={value}
        interactive
        legendBottomOffset={24}
        interactiveZones={interactiveZones}
        authenticatedUser={authenticatedUser}
        isPrivacyMode={isPrivacyMode}
        resourceColors={resourceColors}
        zoneColors={zoneColors}
        isDark={isDark}
      />
    </div>
  );
};
